import { CustomizableListMenu } from '@openx/components-core/lib/CustomizableListMenu/CustomizableListMenu';
import type { BaseRow, Columns, TableColumn } from '@openx/types/tableHelpers';
import { type JSX, useMemo } from 'react';
import { pinFrozenColumns } from './sections/utils';

export interface ColumnFilterProps<RowT extends BaseRow> {
  columns: Columns<RowT>;
  originalColumns: Columns<RowT>;
  handleFilterColumn: (column: TableColumn<RowT>) => void;
  updateColumns: (columns: Columns<RowT>) => void;
  updateLocalStorage: (columns: Columns<RowT>) => void;
}

export const ColumnFilter = <RowT extends BaseRow>({
  handleFilterColumn,
  updateColumns,
  updateLocalStorage,
  columns,
  originalColumns,
}: ColumnFilterProps<RowT>): JSX.Element => {
  const selectColumn = useMemo(() => columns.find(col => col.key === 'select'), [columns]);

  const columnsWithId = useMemo(
    () =>
      selectColumn
        ? columns
            .map(column => {
              return { id: column.key, ...column };
            })
            .filter(col => col.key !== 'select')
        : columns.map(column => {
            return { id: column.key, ...column };
          }),
    [selectColumn, columns]
  );

  const handleItemsChange = (newItems: typeof columnsWithId) => {
    const removeColumnId = newItems.map(({ id, ...props }) => props);
    if (selectColumn) {
      removeColumnId.unshift(selectColumn);
    }

    const pinnedColumns = pinFrozenColumns(originalColumns, removeColumnId as Columns<RowT>);

    updateColumns(pinnedColumns);
    updateLocalStorage(pinnedColumns);
  };

  const handleItemClick = (item: (typeof columnsWithId)[0]) => {
    const column = columns.find(col => col.key === item.key);
    if (column && !column.disabled) {
      handleFilterColumn(column);
    }
  };

  return (
    <CustomizableListMenu
      items={columnsWithId}
      onItemsChange={handleItemsChange}
      onItemClick={handleItemClick}
      getItemLabel={item => (typeof item.title === 'string' ? item.title : item.key)}
      testIdPrefix="column-filter"
      buttonAriaLabel="Customize columns"
    />
  );
};
